import React, { useMemo } from 'react'

import {
  DatasetType,
  FormattedWidgetData,
  FormattedWidgetDataDataset,
  KpiFormat,
  ParsedSegmentPath,
  ParsedWidgetDataLabel,
  StatusData,
  TWidgetSettings,
  WidgetOrder
} from 'types/GlobalWidget'
import {
  TableRow,
  TableColumn,
  TableCell
} from 'components_new/molecules/Table'

import {
  getAllPrefixes,
  getOrderByArray,
  getSortIndexes,
  pathKey,
  sortFormattedDatasets,
  getStatus
} from 'components_new/molecules/Table/utils'
import {
  sortAlphabeticalAsc,
  sortAlphabeticalDesc,
  sortNumericAsc,
  sortNumericDesc
} from 'utils/sortHelper'

import { useTranslation } from 'language/translator'

import {
  TOTAL_COLUMN_ID,
  applyVariantOptionFormat,
  displaysComparison,
  getKpiFormat
} from 'utils/widgetData'

import {
  ORDER_BY_SEGMENT_BY,
  mapDatasetTypeToTableDataType,
  mapStatusDataToTableStatus
} from './utils'

interface Params {
  formattedData: FormattedWidgetData
  expandedPathSet: Set<string>
  order: WidgetOrder
  horizontal: boolean
  showLinks: boolean
  showTotal: boolean
  // the direction each label's backend index runs in relative to the
  // segment order; null when the index says nothing about it
  segmentIndexOrder: 'asc' | 'desc' | null
}

export type WidgetTableRow = {
  label: {
    display_label: string
    segments: ParsedSegmentPath[]
    path: string
    depth: number
    kpi_option_id?: string | null
    variant_option_id?: string | null
  }
  link: string | null
  // set for horizontal tables
  kpi_option_id?: string
}

export type WidgetTableColumn = {
  id: string
  index: number
  label: string
  percentages?: (number | null)[]
  prettyData: (string | number | number[] | string[] | null)[]
  statuses: StatusData[]
  data: (number | string[] | number[] | null)[]
  total: string | number | null
  totalPercentageDiff?: number | null

  // holds the total of every row rather than one segment's values
  isTotal?: boolean

  // holds one variant's values, which carries its own format
  variantOptionId?: string
}

interface Output {
  datasets: FormattedWidgetDataDataset[]
  columns: WidgetTableColumn[]
  rows: WidgetTableRow[]
  longestPath: number
}

function getFilteredIndexes(
  labels: ParsedWidgetDataLabel[],
  expandedPathSet: Set<string>,
  sortRanks: number[] | null,
  expandableMetricRows: boolean
) {
  const visibleSet = new Set<number>()

  labels.forEach((label, index) => {
    const prePath = label.path.map((p) => p.display_label)

    const isVisible =
      prePath.length === 0 ||
      getAllPrefixes(prePath).every((prefix) =>
        expandedPathSet.has(pathKey(prefix))
      )

    if (isVisible) {
      visibleSet.add(index)
    }
  })

  // Map from parent path key to ordered list of visible children indices
  const childrenMap = new Map<string, number[]>()

  labels.forEach((label, index) => {
    if (!visibleSet.has(index)) return

    const parentKey = pathKey(label.path.map((p) => p.display_label))

    if (!childrenMap.has(parentKey)) {
      childrenMap.set(parentKey, [])
    }

    childrenMap.get(parentKey)?.push(index)
  })

  // Sort siblings within each group according to the active order.
  // Sorting the flattened list directly would mix parents and children
  // from different levels and break the hierarchy.
  if (sortRanks) {
    childrenMap.forEach((children) => {
      children.sort((a, b) => {
        // metric rows keep the order of the widget's metrics, so the
        // income statement structure survives sorting on a column
        if (
          expandableMetricRows &&
          (labels[a].path.length === 0 || labels[b].path.length === 0)
        ) {
          return a - b
        }

        return sortRanks[a] - sortRanks[b]
      })
    })
  }

  // DFS traversal to output indices with parents always before their children,
  // regardless of how the backend orders them in the labels array
  const result: number[] = []

  const visit = (parentKey: string) => {
    const children = childrenMap.get(parentKey) ?? []

    children.forEach((index) => {
      result.push(index)

      const label = labels[index]
      const selfKey = pathKey([
        ...label.path.map((p) => p.display_label),
        label.display_label
      ])

      visit(selfKey)
    })
  }

  visit('')

  return result
}

/**
 * The total of every row, as the column a horizontal table shows it
 * in. Each metric is a row here, so a metric's own total is the value
 * on its row.
 */
function getClientTotalColumn(
  filteredDatasets: FormattedWidgetDataDataset[]
): WidgetTableColumn {
  return {
    id: TOTAL_COLUMN_ID,
    index: -1,
    label: '',
    data: filteredDatasets.map((dataset) => dataset.totalRaw),
    prettyData: filteredDatasets.map((dataset) => dataset.total),
    statuses: filteredDatasets.map(() => null),
    total: null,
    isTotal: true
  }
}

function flipHorizontal(
  filteredDatasets: FormattedWidgetDataDataset[],
  rows: WidgetTableRow[],
  columns: WidgetTableColumn[],
  showTotal: boolean
): Output {
  const totalColumn = showTotal ? [getClientTotalColumn(filteredDatasets)] : []
  const flippedColumns = rows.map((row, i) => ({
    id: row.label.display_label,
    index: i,
    data: columns.map((dataset) => dataset.data[i]),
    label: row.label.display_label,
    percentages: columns.map((dataset) =>
      dataset?.percentages ? dataset?.percentages[i] : null
    ),
    prettyData: columns.map((dataset) => dataset.prettyData[i]),
    statuses: columns.map((dataset) => dataset.statuses[i]),
    total: null
  }))

  return {
    datasets: filteredDatasets,
    columns: totalColumn.concat(flippedColumns),
    rows: columns.map((dataset) => {
      return {
        label: {
          segments: [],
          path: '',
          depth: 0,
          display_label: dataset.label
        },
        link: null,
        kpi_option_id: dataset.id
      }
    }),
    longestPath: 0
  }
}

/**
 * Sort horizontal table data by a column's values.
 * In horizontal mode, columns are the original rows (segments) and
 * rows are the original columns (KPIs/datasets).
 */
function sortHorizontalData(data: Output, order: WidgetOrder): Output {
  if (!order.id) {
    // no need for sorting
    return data
  }

  if (order.id === ORDER_BY_SEGMENT_BY) {
    // Sort by segment (first column) - sort rows alphabetically by label
    const sortedIndexes = data.rows
      .map((row, index) => ({ label: row.label.display_label, index }))
      .sort((a, b) =>
        order.dir === 'asc'
          ? sortAlphabeticalAsc(a.label, b.label)
          : sortAlphabeticalDesc(a.label, b.label)
      )
      .map((item) => item.index)

    return {
      ...data,
      rows: sortedIndexes.map((i) => data.rows[i]),
      columns: data.columns.map((col) => ({
        ...col,
        data: sortedIndexes.map((i) => col.data[i]),
        prettyData: sortedIndexes.map((i) => col.prettyData[i]),
        statuses: sortedIndexes.map((i) => col.statuses[i]),
        percentages: col.percentages
          ? sortedIndexes.map((i) => col.percentages?.[i] ?? null)
          : undefined
      }))
    }
  }

  // Find column: try exact match first, then fallback to id only
  let sortColumnIndex = data.columns.findIndex(
    (col) => col.id === order.id && col.index === order.index
  )

  if (sortColumnIndex === -1) {
    sortColumnIndex = data.columns.findIndex((col) => col.id === order.id)
    if (sortColumnIndex === -1) return data
  }

  const sortColumn = data.columns[sortColumnIndex]

  // Get sort indexes based on the column's data values
  const sortedIndexes = sortColumn.data
    .map((value, index) => ({ value, index }))
    .sort((a, b) =>
      order.dir === 'asc'
        ? sortNumericAsc(a.value as number, b.value as number)
        : sortNumericDesc(a.value as number, b.value as number)
    )
    .map((item) => item.index)

  // Apply sort indexes to rows and all column data
  return {
    ...data,
    rows: sortedIndexes.map((i) => data.rows[i]),
    columns: data.columns.map((col) => ({
      ...col,
      data: sortedIndexes.map((i) => col.data[i]),
      prettyData: sortedIndexes.map((i) => col.prettyData[i]),
      statuses: sortedIndexes.map((i) => col.statuses[i]),
      percentages: col.percentages
        ? sortedIndexes.map((i) => col.percentages?.[i] ?? null)
        : undefined
    }))
  }
}

export function useFilteredAndSortedWidgetData({
  formattedData,
  expandedPathSet,
  order,
  horizontal,
  showLinks,
  showTotal,
  segmentIndexOrder
}: Params): Output {
  // the backend nests the deeper segments under each metric when the
  // table is horizontal and has a segment hierarchy, so a row at
  // depth 0 is a metric
  const expandableMetricRows = formattedData.expandableMetricRows

  const flipsOnClient = horizontal && !expandableMetricRows

  const sortingOpt = useMemo(() => {
    // a flipped table is sorted as columns, by sortHorizontalData
    if (flipsOnClient) {
      return null
    }

    return getOrderByArray(
      order,
      formattedData.labels,
      formattedData.datasets,
      segmentIndexOrder
    )
  }, [
    flipsOnClient,
    order,
    formattedData.labels,
    formattedData.datasets,
    segmentIndexOrder
  ])

  const sortRanks = useMemo(() => {
    // No sorting, keep original order
    if (!sortingOpt) return null

    // Map each label index (in the full label list) to its sorted position,
    // so that siblings can be sorted within their group without
    // detaching children from their parents.
    const ranks: number[] = new Array(formattedData.labels.length)

    getSortIndexes(sortingOpt, order.dir).forEach((labelIndex, position) => {
      ranks[labelIndex] = position
    })

    return ranks
  }, [sortingOpt, order.dir, formattedData.labels])

  const filteredIndexes = useMemo(() => {
    return getFilteredIndexes(
      formattedData.labels,
      expandedPathSet,
      sortRanks,
      expandableMetricRows
    )
  }, [formattedData.labels, expandableMetricRows, expandedPathSet, sortRanks])

  const filteredLabels = useMemo(() => {
    return filteredIndexes.map((i) => formattedData.labels[i])
  }, [filteredIndexes, formattedData.labels])

  const filteredLinks = useMemo(() => {
    return filteredIndexes.map((i) =>
      formattedData.links.length > 0 ? formattedData.links[i] : null
    )
  }, [filteredIndexes, formattedData.links])

  const filteredDatasets = useMemo(
    () => sortFormattedDatasets(formattedData.datasets),
    [formattedData.datasets]
  )

  const columns = useMemo(() => {
    return filteredDatasets.map((dataset): WidgetTableColumn => {
      return {
        id: dataset.id,
        index: dataset.index,
        label: dataset.label,
        data: filteredIndexes.map((i) => dataset.data[i]),
        prettyData: filteredIndexes.map((i) => dataset.prettyData[i]),
        statuses: filteredIndexes.map((i) => dataset.statuses[i]),
        percentages: dataset.percentages
          ? filteredIndexes.map((i) => dataset.percentages?.[i] || null)
          : undefined,
        total: dataset.total,
        totalPercentageDiff: dataset.totalPercentageDiff,
        isTotal: dataset.isTotal,
        variantOptionId: dataset.variantOptionId
      }
    })
  }, [filteredDatasets, filteredIndexes])

  const rows = useMemo(() => {
    return filteredLabels.map((label, i) => {
      const isTopLevel = label.path.length === 0

      // a metric heads its rows without being a segment, so it is
      // left out of what they filter on
      const segmentPath = expandableMetricRows
        ? label.path.slice(1)
        : label.path

      // the metric row itself stands for no segment value
      const isMetricRow = expandableMetricRows && isTopLevel

      return {
        label: {
          display_label: label.display_label,
          depth: label.path.length,
          kpi_option_id: label.kpi_option_id,
          variant_option_id: label.variant_option_id,
          segments: isMetricRow
            ? []
            : [
                ...segmentPath,
                {
                  ...label,
                  attribute_option_id: label.attribute_option_id,
                  label: label.label,
                  period: label.period
                }
              ],
          path: pathKey([
            ...label.path.map((value) => value.display_label),
            label.display_label
          ])
        },
        kpi_option_id: label.kpi_option_id ?? undefined,
        link: showLinks && isTopLevel ? filteredLinks[i] ?? null : null
      }
    })
  }, [filteredLabels, filteredLinks, expandableMetricRows, showLinks])

  const longestPath = useMemo(() => {
    return formattedData.labels.reduce((max, label) => {
      return Math.max(max, label.path.length)
    }, 0)
  }, [formattedData.labels])

  if (flipsOnClient) {
    const flippedData = flipHorizontal(
      filteredDatasets,
      rows,
      columns,
      showTotal
    )

    return sortHorizontalData(flippedData, order)
  }

  return {
    datasets: filteredDatasets,
    columns: columns,
    rows,
    longestPath
  }
}

interface TransformParams {
  columns: WidgetTableColumn[]
  datasets: FormattedWidgetDataDataset[]
  formattedData: FormattedWidgetData
  isComparativeButIllegal: boolean
  onCellClick?: (
    row: WidgetTableRow,
    event: React.MouseEvent<HTMLElement>
  ) => void
  settings: TWidgetSettings
  widgetRows: WidgetTableRow[]
}

interface TransformOutput {
  genericColumns: TableColumn[]
  genericRows: TableRow[]
  parentPathsSet: Set<string>
}

/**
 * Transform widget-specific table data to generic Table molecule format
 */
export function useTransformedTableData({
  columns,
  datasets,
  formattedData,
  isComparativeButIllegal,
  onCellClick,
  settings,
  widgetRows
}: TransformParams): TransformOutput {
  const { t } = useTranslation()

  // With metrics as rows a column spans every metric, so a row's own
  // metric decides how its value is written, which unit follows it
  // and where its thresholds are.
  const formatByMetric = useMemo(() => {
    if (!formattedData.expandableMetricRows) {
      return null
    }

    const type = datasets[0]?.type ?? DatasetType.NUMBER

    return new Map(
      settings.kpi_options.map((kpi) => [
        kpi.id,
        getKpiFormat(kpi, type, kpi.unit)
      ])
    )
  }, [formattedData.expandableMetricRows, datasets, settings.kpi_options])

  // Transform WidgetTableColumn[] to generic TableColumn[]
  // with pre-formatted cells
  const genericColumns: TableColumn[] = useMemo(() => {
    return columns.map((col) => {
      const dataset = settings.horizontal ? datasets[0] : datasets[col.index]
      const unit = dataset?.unit ?? ''

      // a variant table writes a cell as its metric, overridden by
      // its comparison figure: the column carries the figure in the
      // horizontal layout and the row carries it in the vertical one
      const columnVariant = col.variantOptionId
        ? settings.variant_options.find(
          (option) => option.id === col.variantOptionId
        )
        : undefined

      const getVariantCellFormat = (
        rowLabel: WidgetTableRow['label'] | undefined
      ): KpiFormat | undefined => {
        const rowVariant = rowLabel?.variant_option_id
          ? settings.variant_options.find(
            (option) => option.id === rowLabel.variant_option_id
          )
          : undefined

        // the metric sits on the row when the figure is the column,
        // and on the column when the figure is the row
        const kpiOptionId = columnVariant
          ? rowLabel?.kpi_option_id
          : dataset?.kpiOptionId

        const base = formatByMetric?.get(kpiOptionId ?? '')
        const variant = columnVariant ?? rowVariant
        const cellKpi = settings.kpi_options.find(
          (option) => option.id === kpiOptionId
        )

        if (!base || !variant || !cellKpi) {
          return base
        }

        return applyVariantOptionFormat(base, cellKpi, variant)
      }

      // Build cells array with all formatting done
      const cells: TableCell[] = col.prettyData.map((prettyValue, rowIndex) => {
        // A dataset is a KpiFormat, so a cell reads both the same way
        const cellFormat: KpiFormat | undefined = formatByMetric
          ? getVariantCellFormat(widgetRows[rowIndex]?.label)
          : settings.horizontal
            ? datasets[rowIndex]
            : dataset

        const datasetUnit = cellFormat?.unit ?? ''
        const defaultPrettyValue = datasetUnit === '%' ? 'N/A' : ''
        const finalPrettyValue = prettyValue ?? defaultPrettyValue
        const showDiff = displaysComparison(
          cellFormat?.comparativeDisplayTypeSelected
        )
        const percentageDiff = col.percentages?.[rowIndex]
        const status = col.statuses[rowIndex]

        // Calculate showTrendTag (all logic here, not in molecule)
        const showTrendTag = Boolean(
          showDiff &&
            percentageDiff &&
            cellFormat?.increaseIsPositive !== undefined &&
            !isComparativeButIllegal
        )

        // Calculate color based on type and thresholds
        let color = 'text.primary'
        let formattedLabel: string | string[]

        if (cellFormat?.type === DatasetType.NUMBER) {
          // Format: "1234 kr"
          formattedLabel =
            prettyValue !== null
              ? `${finalPrettyValue} ${datasetUnit}`.trim()
              : ''

          // Calculate color from thresholds
          if (cellFormat.upperBoundThreshold !== null) {
            const rawValue = col.data[rowIndex]
            const statusColor = getStatus(
              rawValue as number,
              cellFormat.upperBoundThreshold,
              cellFormat.lowerBoundThreshold,
              cellFormat.increaseIsPositive
            )

            color = `${statusColor}.text`
          }
        } else if (cellFormat?.type === DatasetType.NUMBER_TAG) {
          // Format: array of strings with unit
          formattedLabel = (finalPrettyValue as string[]).map((val) =>
            datasetUnit ? `${val} ${datasetUnit}` : val
          )
        } else {
          // Other types: use as is
          formattedLabel = finalPrettyValue as string[]
        }

        return {
          label: formattedLabel,
          color,
          percentageDiff,
          showDiff,
          showTrendTag,
          increaseIsPositive: cellFormat?.increaseIsPositive,
          status: mapStatusDataToTableStatus(status)
        }
      })

      // Same comparison logic as body cells, applied to the total/summary row
      const totalShowDiff = displaysComparison(
        dataset?.comparativeDisplayTypeSelected
      )
      const totalPercentageDiff = col.totalPercentageDiff
      const totalShowTrendTag = Boolean(
        totalShowDiff &&
          totalPercentageDiff &&
          dataset?.increaseIsPositive !== undefined &&
          !isComparativeButIllegal
      )

      return {
        id: col.id,
        index: col.index,
        isTotal: col.isTotal,
        label: col.isTotal ? t('widgets', 'total_column') : col.label,
        type: mapDatasetTypeToTableDataType(dataset.type),
        cells,
        total: col.total !== null ? `${col.total} ${unit}`.trim() : undefined,
        totalPercentageDiff,
        totalShowTrendTag,
        totalIncreaseIsPositive: dataset?.increaseIsPositive
      }
    })
  }, [
    columns,
    datasets,
    formatByMetric,
    settings.horizontal,
    isComparativeButIllegal,
    t,
    widgetRows
  ])

  // Pre-calculate which paths have children (performance optimization)
  const parentPathsSet = useMemo(() => {
    const paths = new Set<string>()

    formattedData.labels.forEach((label) => {
      // Add all parent paths
      for (let i = 0; i < label.path.length; i++) {
        const parentPath = label.path
          .slice(0, i + 1)
          .map((p) => p.display_label)
          .join('__SEP__')

        paths.add(parentPath)
      }
    })

    return paths
  }, [formattedData.labels])

  // Translate WidgetTableRow to generic TableRow with optional onCellClick
  const genericRows: TableRow[] = useMemo(() => {
    return widgetRows.map((row) => {
      // Check if this row's path is in the parent paths set (O(1) lookup)
      const hasChildren = parentPathsSet.has(row.label.path)

      // Determine if this cell should have click handler (if callback
      // provided). A row is worth opening when it stands for a
      // segment value to filter on, or for a metric of its own.
      const allowOnClick = row.label.segments.length > 0 || !!row.kpi_option_id

      const cellClickHandler =
        onCellClick && allowOnClick
          ? (event: React.MouseEvent<HTMLElement>) => {
              onCellClick(row, event)
            }
          : undefined

      return {
        label: {
          display_label: row.label.display_label,
          segmentDepth: row.label.depth,
          path: row.label.path,
          hasChildren
        },
        link: row.link,
        onCellClick: cellClickHandler
      }
    })
  }, [widgetRows, parentPathsSet, onCellClick])

  return {
    genericColumns,
    genericRows,
    parentPathsSet
  }
}
